import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetUserQuery,
  useLoginMutation,
  useLogOutUserMutation,
} from "../../store/auth/usersApiSlice";
import {
  clearIdentityScopedState,
  clearPendingReauthIdentity,
  clearUserInfo,
  hideIdleModal,
  hideLoginModal,
  logout,
  setExpireTime,
  setToken,
  setUserInfo,
} from "../../store/auth/authSlice";
import { AUTH_EVENTS, postAuthEvent } from "../../utils/authChannel";
import { CommonToast } from "../../components/common/Toast";
import { validateEmail } from "../../utils/Helper";
import ReactGA from "react-ga4";
import { useGetMagicTokenMutation } from "../../store/Company/companyApiSlice";
import { plansInformation } from "../../constants/constants";

/**
 * Sign-in behaviour for the login screen, lifted out of pages/public/Login.jsx
 * so a redesigned screen can present it differently without a second copy of
 * the rules.
 *
 * Everything here is TRANSCRIBED from that page, not rewritten: the same
 * validation, the same request payload, the same TIP-511 identity guards, the
 * same redirect precedence and the same analytics events, in the same order.
 * The only change is the shape — state and handlers are returned instead of
 * rendered. pages/public/Login.jsx still holds its own copy and is untouched;
 * pointing it at this hook is a separate change.
 *
 * Callers own presentation only.
 */
export default function useLoginForm({ modalLogin = false, preventRoute = false } = {}) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [formData, setFormData] = useState({});
  const [showSessionPopup, setShowSessionPopup] = useState(false);
  // Device and last-seen time of the OTHER session, so the conflict dialog can
  // name what it is about to sign out instead of saying "another device".
  const [sessionConflict, setSessionConflict] = useState(null);
  const { token, pendingReauthEmail } = useSelector((state) => state.auth);
  // TIP-511 — inside the "Login Required" modal the session belongs to a known
  // account, so the email is pinned to it. `readOnly` is UX; what makes the pin
  // hold against a devtools edit is that the field becomes a CONTROLLED input
  // and `handleLogin` reads `formData.email`, never the DOM node. The mismatch
  // check in `handleLogin` is the backstop for that invariant.
  const lockedEmail = modalLogin ? pendingReauthEmail : null;
  const location = useLocation();
  const redirectStep = location?.state?.redirectToStep ?? null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isArticle = params.get("app") === "article";
  const articleSlug = params.get("slug");
  const inviteCodeParams = params.get("invite_code");
  const [inviteCode, setInviteCode] = useState(localStorage.getItem("invite_code"));
  const promptPage = location.pathname.includes("prompt");
  const dashboardPage = location.pathname.includes("dashboard");
  const lastVisited = localStorage.getItem("lastVisited") ?? "";
  const [error, setError] = useState("");
  const [login, { isLoading }] = useLoginMutation();
  const [logOutUser] = useLogOutUserMutation();
  const [userToken, setUserToken] = useState("");
  const [getMagicToken] = useGetMagicTokenMutation();
  const { userInfo } = useSelector((state) => state.auth);
  const { data, isSuccess } = useGetUserQuery(userToken, {
    skip: !userToken,
    refetchOnMount: true,
  });
  const [getSearchParams] = useSearchParams();
  // TIP-599 — set once handleLogin has decided where a fresh sign-in should
  // land (claim flow, stored lastVisited, plans resume, ...). The mount effect
  // below is the "already signed in, don't sit on /login" guard; without this
  // it also fires moments later — when userInfo arrives — and overwrites that
  // decision with a bounce to Home, losing the claim intent.
  const loginRoutedRef = useRef(false);

  const articleRedirect = (magicToken) => {
    const baseUrl = `${process.env.REACT_APP_K_HUB_URL}/magic/${magicToken}`;
    // Only append slug if it's available
    const url = articleSlug ? `${baseUrl}?slug=${articleSlug}` : baseUrl;
    window.open(url, "_blank");
  };

  // TIP-511 — seed the pinned email so a plain "enter password + submit" works.
  useEffect(() => {
    if (lockedEmail) {
      setFormData((prev) => ({ ...prev, email: lockedEmail }));
    }
  }, [lockedEmail]);

  useEffect(() => {
    if (inviteCodeParams) {
      setInviteCode(inviteCodeParams);
    }
  }, [inviteCodeParams]);

  useEffect(() => {
    if (userInfo) {
      if (userInfo?.is_first_time_setted_password) {
        navigate(`/reset-password?token=${userToken}&email=${userInfo?.email}`);
      } else {
        if (!isArticle && !preventRoute && !loginRoutedRef.current) {
          navigate(promptPage ? `/gpt-prompts/access` : `/`);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, userInfo]);

  useEffect(() => {
    const redirect = async () => {
      if (isArticle) {
        const res = await getMagicToken({ token });
        if (res?.data?.token) {
          articleRedirect(res?.data?.token);
          navigate(dashboardPage ? `/dashboard` : `/`);
        } else {
          dispatch(clearUserInfo());
        }
      }
    };
    redirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArticle, token]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(setUserInfo({ ...data }));
    }
  }, [data, dispatch, isSuccess]);

  function togglePasswordVisibility() {
    setIsPasswordVisible((prevState) => !prevState);
  }

  const handleChange = (event) => {
    // TIP-511 — while re-authenticating a known session the email is fixed.
    if (lockedEmail && event.target.name === "email") return;
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  /**
   * TIP-511 — leave the dead session behind entirely. This is the supported
   * route for a second user on the same browser: a full logout (which does
   * clear everything) followed by a normal sign-in.
   */
  const handleSwitchAccount = async () => {
    // END THE SERVER SESSION TOO, not just ours.
    // Clearing Redux and localStorage and stopping there is not "a full
    // logout": the backend keeps the OutstandingToken alive, and sign-in is
    // single-session — so the very next login answers ALREADY_LOGGED_IN and the
    // customer lands in the session-conflict dialog instead of signing in.
    // Switching account then appears broken when what is broken is the logout
    // Best effort: the token that got us here may already be rejected, in which
    // case the endpoint refuses and there is nothing more the client can do.
    // Clearing locally must happen regardless, or the customer is stuck in a
    // modal they cannot leave.
    try {
      // `token` is the LIVE session from Redux, not the local `userToken`
      // state — that one is only set after a successful sign-in here, and is
      // empty at the moment somebody switches account.
      await logOutUser({ token }).unwrap();
    } catch (err) {
      // Already expired, already blacklisted, or offline. Not fatal here.
    }

    // Tell the other tabs before clearing this one. They share the cookies
    // that were just revoked, so without this they keep rendering a signed-in
    // UI until their next 401 — which on an idle page may never come.
    postAuthEvent(AUTH_EVENTS.SIGNED_OUT);

    dispatch(hideLoginModal());
    dispatch(hideIdleModal());
    dispatch(logout());
    dispatch(clearUserInfo());
    setFormData({});
    setError("");
    navigate("/login");
  };

  const handleLogin = async (e, forced = false) => {
    e.preventDefault();
    try {
      if (!validateEmail(formData.email)) {
        setError("Invalid Email, Please enter a valid email");
      } else {
        setError("");
        const submittedEmail = formData.email.toLowerCase();

        /**
         * TIP-511 — backstop. The controlled field should make this
         * unreachable; if it ever is reached (the field made uncontrolled
         * again, or `lockedEmail` changing after the user typed), a mismatch
         * must never authenticate a second identity into the surviving
         * session. Drop to a clean logout instead — the supported way to
         * switch accounts on a shared browser.
         */
        if (lockedEmail && submittedEmail !== lockedEmail) {
          handleSwitchAccount();
          CommonToast.error(
            "Please log in as " + lockedEmail + ", or log out to use another account."
          );
          return;
        }

        const previousEmail = userInfo?.email ? userInfo.email.toLowerCase() : null;
        const splittedData = lastVisited ? lastVisited?.split("/") : [];
        const res = await login({
          email: formData.email.toLowerCase(),
          password: formData.password,
          ...(forced && { force_login: true }),
          ...(isArticle && { return_magic_token: true }),
          ...(inviteCode && { invite_code: inviteCode }),
          ...(splittedData?.[1] === "claim" && { validate_id: splittedData[2] }),
          ...(modalLogin && { referance: splittedData[2] }),
        }).unwrap();

        if (res?.status) {
          ReactGA.event({
            category: "Login",
            action: "Login Flow:Attempted to Login; Login Failed",
            label: "Attempted to Login; Login Failed",
          });
          if (res?.status === "LOGIN_FAILED") {
            // STOP HERE. Without the return this falls through to the success
            // path below, which reads `res.token` (undefined), dispatches
            // setToken(undefined), raises a misleading "Token is required"
            // toast and navigates to /login — remounting the screen and wiping
            // the very error just set. A customer with a wrong password sees a
            // bounce and no reason.
            setError(res?.message ? res?.message : "Validation Failed");
            return;
          } else if (res?.status === "ALREADY_LOGGED_IN") {
            setSessionConflict({
              device: res?.device ?? null,
              last_seen_at: res?.last_seen_at ?? null,
            });
            setShowSessionPopup(true);
            return;
          }
        }
        if (res?.reset_token) {
          ReactGA.event({
            category: "Login",
            action: "Login Flow:Asked user to reset your password",
            label: "Asked user to reset your password",
          });
          navigate(
            `/reset-password?token=${res?.reset_token}&email=${formData.email.toLowerCase()}`
          );
        } else {
          /**
           * TIP-511 — defence in depth. Whichever route got us here, if the
           * account that just signed in is not the one this browser was last
           * holding state for, that state belongs to someone else: drop the
           * identity-scoped keys before any of it can be rendered. Same-user
           * re-login takes the else branch and keeps its in-progress work.
           */
          if (previousEmail && previousEmail !== submittedEmail) {
            dispatch(clearIdentityScopedState());
          } else {
            dispatch(clearPendingReauthIdentity());
          }

          /* Success is no longer "the response contained a token". */
          const signedIn = Boolean(res) && !res?.status;
          const loginToken = signedIn;

          dispatch(setExpireTime(res?.expire_time));
          setUserToken(loginToken);
          dispatch(setToken());

          // Tell the other tabs, and say WHO — a tab holding user A's cached
          // data needs to know that user B now owns this browser.
          if (signedIn) {
            postAuthEvent(AUTH_EVENTS.SIGNED_IN, submittedEmail);
          }

          if (loginToken) {
            ReactGA.event({
              category: "Login",
              action: "Login Flow:Attempted to Login; Login Success",
              label: "Attempted to Login; Login Success",
            });
            if (isSuccess) {
              dispatch(setUserInfo({ ...data }));
              setShowSessionPopup(false);
              dispatch(hideLoginModal());
              dispatch(hideIdleModal());
            }
            const slug = localStorage.getItem("slug");
            // This sign-in owns the redirect from here on — see loginRoutedRef.
            loginRoutedRef.current = true;
            if (redirectStep) {
              navigate("/sign-up", {
                state: { redirectToStep: 6 },
              });
            } else if (slug && lastVisited == "/plans") {
              let plan = plansInformation.filter((item) => item.slug === slug);
              const companyDataString = localStorage.getItem("payasyougoCompanyID");
              window.scrollTo(0, 0);
              navigate("/plans/paymentdetails", {
                state: {
                  planDetails: plan[0],
                  company_id: JSON.parse(JSON.stringify(companyDataString)),
                },
              });
            } else if (lastVisited) {
              if (res?.validate_id === true) {
                navigate(`/search/${splittedData[2]}`);
              } else {
                navigate(`${lastVisited}`);
              }
            } else {
              if (isArticle) {
                articleRedirect(res.magic_token);
              }
              if (preventRoute) {
                dispatch(hideLoginModal());
              } else {
                navigate(dashboardPage ? `/dashboard` : `/`);
              }
            }
          } else {
            CommonToast.error("Token is required");
            const url =
              isArticle && articleSlug
                ? `/login?app=article&slug=${articleSlug}`
                : "/login?app=article";
            navigate(dashboardPage ? "/login/dashboard" : isArticle ? url : "/login");
          }
        }
      }
    } catch (err) {
      setError(
        err?.data ? Object.values(err?.data)[0] : "An error occurred. Please try again later."
      );
    }
  };

  const handleForgotPassword = () => {
    ReactGA.event({
      category: "Fogot Password",
      action: "Login Flow:Clicked on forgot password button",
      label: "Clicked on forgot password button",
    });
  };

  const handleCreateNewAccount = () => {
    ReactGA.event({
      category: "Create New Account",
      action: "Login Flow:Clicked on Create New Account button",
      label: "Clicked on Create New Account button",
    });
  };

  return {
    formData,
    handleChange,
    error,
    isLoading,
    isPasswordVisible,
    togglePasswordVisibility,
    lockedEmail,
    handleLogin,
    handleSwitchAccount,
    showSessionPopup,
    sessionConflict,
    setShowSessionPopup,
    handleForgotPassword,
    handleCreateNewAccount,
    getSearchParams,
  };
}
